import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MetricsChart } from '../../components/observability/MetricsChart';
import { observabilityService, type MetricResult } from '../../services/observability.service';
import api from '../../services/api';
import { RefreshCw, Clock, Shield, CheckCircle, XCircle, Activity, Info } from 'lucide-react';
import { useSettingsStore } from '../../stores/settingsStore';
import { formatDate, formatTimeOnly } from '../../utils/formatDate';
import { isSystemAcl } from '../../utils/aclUtils';
import EdgeSelector from '../../components/common/EdgeSelector';

interface ACLConfigSummary {
  edgeId: string;
  hasAcls: boolean;
  totalAcls: number;
  totalRules: number;
  acls: Array<{
    name: string;
    permitRules: number;
    denyRules: number;
    interfaces: string[];
  }>;
  lastConfigUpdate: string | null;
}

interface ACLSummary {
  aclName: string;
  permitRules: number;
  denyRules: number;
  packetsPermit: number;
  packetsDeny: number;
  bytesPermit: number;
  bytesDeny: number;
}

const ACLMonitoring: React.FC = () => {
  const [searchParams] = useSearchParams();
  const edgeIdParam = searchParams.get('edgeId');

  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>(edgeIdParam || '');

  // ACL Config State (from DB — always available)
  const [configSummary, setConfigSummary] = useState<ACLConfigSummary | null>(null);

  // ACL Metrics State (from PromQL — only when edge is online)
  const [aclSummaries, setAclSummaries] = useState<ACLSummary[]>([]);
  const [packetsHistData, setPacketsHistData] = useState<any[]>([]);
  const [bytesHistData, setBytesHistData] = useState<any[]>([]);

  // Fetch ACL config from DB (always accurate, even when edge is offline)
  const fetchConfigSummary = async () => {
    if (!selectedEdgeId) return;
    try {
      const response = await api.get(`/api/v2/edges/${selectedEdgeId}/acl/config-summary`);
      const body = response as any;
      if (body.success && body.data) {
        setConfigSummary(body.data);
      }
    } catch (error) {
      console.error('Failed to fetch ACL config summary:', error);
    }
  };

  const fetchData = async (isBackground = false) => {
    if (!selectedEdgeId) return;

    if (!isBackground) setLoading(true);
    try {
      const options = isBackground ? { showLoading: false } : undefined;
      // Fetch DB config and PromQL metrics in parallel
      const end = Math.floor(Date.now() / 1000);
      const start = end - 3600; // Last 1 hour
      const step = 60; // 1 minute resolution

      const [
        packetsPermitRes,
        packetsDenyRes,
        bytesPermitRes,
        bytesDenyRes,
        packetsHistRes,
        bytesHistRes,
      ] = await Promise.all([
        observabilityService.queryRange(
          `edge_acl_packets_matched_total{edge_id="${selectedEdgeId}", action="permit"}`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `edge_acl_packets_matched_total{edge_id="${selectedEdgeId}", action="deny"}`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `edge_acl_bytes_matched_total{edge_id="${selectedEdgeId}", action="permit"}`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `edge_acl_bytes_matched_total{edge_id="${selectedEdgeId}", action="deny"}`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_acl_packets_matched_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
        observabilityService.queryRange(
          `rate(edge_acl_bytes_matched_total{edge_id="${selectedEdgeId}"}[5m])`,
          start, end, step, options
        ),
      ]);

      // Helper to get last non-null value
      const getLastNotNull = (result: MetricResult[]): number | null => {
        if (!result || result.length === 0) return null;
        let lastValue: number | null = null;
        let lastTimestamp = 0;

        for (const series of result) {
          if (series.values && series.values.length > 0) {
            const lastPoint = series.values[series.values.length - 1];
            if (lastPoint && lastPoint[1] !== 'NaN' && lastPoint[1] !== '') {
              const timestamp = lastPoint[0];
              const value = parseFloat(lastPoint[1]);
              if (!isNaN(value) && timestamp > lastTimestamp) {
                lastValue = value;
                lastTimestamp = timestamp;
              }
            }
          }
        }
        return lastValue;
      };

      // Build per-ACL traffic summaries from PromQL (packets/bytes only)
      const summaryMap = new Map<string, ACLSummary>();

      // Seed from DB config so ACLs appear even without traffic metrics
      if (configSummary?.acls) {
        for (const acl of configSummary.acls) {
          summaryMap.set(acl.name, {
            aclName: acl.name,
            permitRules: acl.permitRules,
            denyRules: acl.denyRules,
            packetsPermit: 0,
            packetsDeny: 0,
            bytesPermit: 0,
            bytesDeny: 0,
          });
        }
      }

      // Overlay PromQL traffic data
      const overlayMetric = (res: any, field: keyof ACLSummary) => {
        if (res.status === 'success' && res.data?.result) {
          for (const series of res.data.result) {
            const aclName = series.metric.acl_name || 'default';
            if (!summaryMap.has(aclName)) {
              summaryMap.set(aclName, {
                aclName, permitRules: 0, denyRules: 0,
                packetsPermit: 0, packetsDeny: 0, bytesPermit: 0, bytesDeny: 0,
              });
            }
            (summaryMap.get(aclName)! as any)[field] = getLastNotNull([series]) || 0;
          }
        }
      };

      overlayMetric(packetsPermitRes, 'packetsPermit');
      overlayMetric(packetsDenyRes, 'packetsDeny');
      overlayMetric(bytesPermitRes, 'bytesPermit');
      overlayMetric(bytesDenyRes, 'bytesDeny');

      setAclSummaries(Array.from(summaryMap.values()));

      // Historical charts
      if (packetsHistRes.status === 'success') {
        setPacketsHistData(transformMetricData(packetsHistRes.data.result));
      }
      if (bytesHistRes.status === 'success') {
        setBytesHistData(transformMetricData(bytesHistRes.data.result));
      }

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch ACL metrics:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  // Fetch DB config on edge change, then fetch metrics
  useEffect(() => {
    if (!selectedEdgeId) return;
    fetchConfigSummary();
  }, [selectedEdgeId]);

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(true), 30000); // 30s refresh
    return () => clearInterval(interval);
  }, [selectedEdgeId, configSummary]);

  // Transform metric data for charts
  const transformMetricData = (results: MetricResult[]) => {
    if (!results || results.length === 0) return [];

    const dataMap = new Map<number, any>();

    results.forEach((series) => {
      const aclName = series.metric.acl_name || 'default';
      const action = series.metric.action || 'unknown';
      const seriesLabel = `${aclName} (${action})`;

      series.values?.forEach(([ts, val]) => {
        if (!dataMap.has(ts)) {
          dataMap.set(ts, { timestamp: ts });
        }
        const point = dataMap.get(ts);
        point[seriesLabel] = parseFloat(val);
      });
    });

    return Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
  };

  // Format bytes
  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Colors for charts
  const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  const getDynamicLines = (data: any[], excludeKeys = ['timestamp']) => {
    if (!data || data.length === 0) return [];
    const keys = Object.keys(data[0] || {}).filter(k => !excludeKeys.includes(k));
    return keys.map((k, i) => ({
      dataKey: k,
      name: k,
      color: COLORS[i % COLORS.length]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">ACL Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">Access Control List metrics and statistics</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500 hidden sm:flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            {formatTimeOnly(lastRefreshed, dateSettings)}
          </span>
          <button
            onClick={() => { fetchConfigSummary(); fetchData(false); }}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Edge Selection */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Edge Device</label>
          <EdgeSelector
            selectedEdgeId={selectedEdgeId}
            onEdgeChange={setSelectedEdgeId}
          />
        </div>
      </div>

      {/* Status Cards — sourced from DB config (always accurate) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* ACL Status */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">ACL Status</h3>
            <Shield className="h-5 w-5 text-gray-400" />
          </div>
          <div className={`mt-2 text-lg font-semibold flex items-center ${configSummary?.hasAcls ? 'text-green-600' : 'text-gray-500'}`}>
            <span className={`h-3 w-3 rounded-full mr-2 ${configSummary?.hasAcls ? 'bg-green-500' : 'bg-gray-400'}`}></span>
            {configSummary?.hasAcls ? 'Configured' : 'No ACLs'}
          </div>
        </div>

        {/* ACL Count */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Total ACLs</h3>
            <Shield className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-3xl font-semibold text-blue-600">{configSummary?.totalAcls ?? 0}</p>
        </div>

        {/* Total Rules */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Total Rules</h3>
            <Activity className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-3xl font-semibold text-gray-900">{configSummary?.totalRules ?? 0}</p>
        </div>

        {/* Last Config Update */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Last Config Update</h3>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <p className="mt-2 text-sm font-medium text-gray-600">
            {configSummary?.lastConfigUpdate
              ? formatDate(new Date(configSummary.lastConfigUpdate), dateSettings)
              : 'Never'}
          </p>
        </div>
      </div>

      {/* Info banner for pending counters */}
      {aclSummaries.length > 0 && aclSummaries.every(a => a.packetsPermit === 0 && a.packetsDeny === 0) && (
        <div className="flex items-start gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Info className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">ACL hit counters pending</p>
            <p className="mt-0.5">Packet and byte counters will populate after the next agent update with hitcount collection from VPP ACL plugin tables.</p>
          </div>
        </div>
      )}

      {/* ACL Summary Table */}
      {aclSummaries.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">ACL Statistics by Name</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ACL Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rules</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Packets Matched</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bytes Matched</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {aclSummaries.map((acl) => (
                  <tr key={acl.aclName} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 text-blue-500 mr-2" />
                        <span className="font-medium text-gray-900">{acl.aclName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isSystemAcl(acl.aclName) ? (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">System</span>
                      ) : (
                        <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">User</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <span className="flex items-center text-green-600">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {acl.permitRules} permit
                        </span>
                        <span className="flex items-center text-red-600">
                          <XCircle className="h-4 w-4 mr-1" />
                          {acl.denyRules} deny
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <span className="text-green-600">{Math.round(acl.packetsPermit).toLocaleString()} permit</span>
                        <span className="text-red-600">{Math.round(acl.packetsDeny).toLocaleString()} deny</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <span className="text-green-600">{formatBytes(acl.bytesPermit)} permit</span>
                        <span className="text-red-600">{formatBytes(acl.bytesDeny)} deny</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No ACL configured message — only when DB has no ACLs */}
      {!configSummary?.hasAcls && !loading && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
          <Shield className="h-12 w-12 text-amber-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-amber-800">No ACLs Configured</h3>
          <p className="text-sm text-amber-600 mt-1">
            No access control lists have been configured for this edge. Create ACLs in the edge configuration to see statistics.
          </p>
        </div>
      )}

      {/* Historical Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsChart
          title="ACL Packets Matched Rate"
          data={packetsHistData}
          loading={loading}
          unit=" pps"
          lines={getDynamicLines(packetsHistData)}
        />

        <MetricsChart
          title="ACL Bytes Matched Rate"
          data={bytesHistData}
          loading={loading}
          unit=" B/s"
          lines={getDynamicLines(bytesHistData)}
        />
      </div>
    </div>
  );
};

export default ACLMonitoring;
